import {type Emoji} from 'mattermost-redux/types/emojis';
import marked from '@time-webkit/marked';

import EmojiMap from './emoji_map';

export function matchEmoticons(text: string): string[] {
    const emojis: string[] = [];

    marked.walkTokens(marked.lexer(text), (token) => {
        if (token.type === 'emoji') {
            emojis.push(token.text);
        }
    });

    return emojis;
}

const DEFAULT_EMOJI_MAP = new EmojiMap(new Map());

export function handleEmoticons(
    text: string,
    tokens: Map<string, {value: string; originalText: string}>,
    emojiMap = DEFAULT_EMOJI_MAP,
): string {
    let output = text;

    let startFrom = 0;
    marked.walkTokens(marked.lexer(text), (token) => {
        if (token.type === 'text') {
            startFrom = output.indexOf(token.raw) + token.raw.length;
            return;
        }

        if (token.type !== 'emoji') {
            return;
        }

        const index = tokens.size;
        const alias = `$TIME_EMOTICON${index}$`;

        const emojiName = token.text.replaceAll(':', '');
        let tokenValue;

        if (token.isUnicode) {
            const emojiCode = getUnicodeEmojiCode(emojiName);

            // convert emoji to image if supported, or wrap in span to apply appropriate formatting
            const isSupported = emojiMap.hasUnicode(emojiCode);

            if (isSupported) {
                const emoji = emojiMap.getUnicode(emojiCode);

                tokenValue = renderEmoji(emoji.short_names[0], token.raw);
            } else {
                tokenValue = renderUnsupportedUnicodeEmoji(token.raw);
            }
        } else {
            tokenValue = renderEmoji(emojiName, token.raw);
        }

        tokens.set(alias, {
            value: tokenValue,
            originalText: token.raw,
        });

        output = output.slice(0, startFrom) +
                output.slice(startFrom).replace(token.raw, alias);

        startFrom = output.indexOf(alias) + alias.length;
    });

    return output;
}

export function renderEmoji(name: string, matchText: string): string {
    return `<span data-emoticon="${name}">${matchText}</span>`;
}

function renderUnsupportedUnicodeEmoji(matchText: string): string {
    return `<span class="emoticon emoticon--unicode">${matchText}</span>`;
}

// if an emoji
// - has `skin_variations` then it uses the default skin (yellow)
// - has `skins` it's first value is considered the skin version (it can contain more values)
// - any other case it doesn't have variations or is a custom emoji.
export function getSkin(emoji: Emoji): string | null {
    if ('skin_variations' in emoji) {
        return 'default';
    }
    if ('skins' in emoji) {
        const skin = emoji?.skins?.[0] ?? '';

        if (skin.length !== 0) {
            return skin;
        }
    }
    return null;
}

export function emojiMatchesSkin(emoji: Emoji, skin: string): boolean {
    const emojiSkin = getSkin(emoji);
    return !emojiSkin || emojiSkin === skin;
}

// Gets the unicode character code of a character starting at the given index in the string
// Adapted from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/charCodeAt
function fixedCharCodeAt(str: string, idx = 0) {
    // ex. fixedCharCodeAt('\uD800\uDC00', 0); // 65536
    // ex. fixedCharCodeAt('\uD800\uDC00', 1); // false
    const code = str.charCodeAt(idx);

    // High surrogate (could change last hex to 0xDB7F to treat high
    // private surrogates as single characters)
    if (code >= 0xD800 && code <= 0xDBFF) {
        const hi = code;
        const low = str.charCodeAt(idx + 1);

        if (isNaN(low)) {
            console.log('High surrogate not followed by low surrogate in fixedCharCodeAt()'); // eslint-disable-line
        }

        return ((hi - 0xD800) * 0x400) + (low - 0xDC00) + 0x10000;
    }

    if (code >= 0xDC00 && code <= 0xDFFF) { // Low surrogate
        // We return false to allow loops to skip this iteration since should have
        // already handled high surrogate above in the previous iteration
        return -1;
    }

    return code;
}

function getUnicodeEmojiCode(emojiName: string) {
    const codePoints = [fixedCharCodeAt(emojiName, 0)];

    for (let i = 1; i < emojiName.length; i++) {
        const codePoint = fixedCharCodeAt(emojiName, i);
        if (codePoint === -1) {
            // Not a complete character
            continue;
        }

        codePoints.push(codePoint);
    }

    return codePoints.map((codePoint) => codePoint.toString(16)).join('-');
}
